import { z } from 'zod';
import { humanSourceUrl } from './source-links';

/**
 * Owner-facing item kinds the compliance agenda can show. Legacy San Francisco
 * record kinds are owned by one source skill each; `skill` is any item a skill
 * projects through the generic action-item descriptor.
 */
export const COMPLIANCE_KINDS = [
  'nov',
  'complaint',
  'fire-violation',
  'fire-inspection',
  '311',
  'lien',
  'permit',
  'tax',
  'fire-filing',
  'skill',
] as const;
export type ComplianceKind = (typeof COMPLIANCE_KINDS)[number];

/** The source skill whose collector produces each legacy item kind. */
export const KIND_OWNERS: Record<Exclude<ComplianceKind, 'skill'>, string> = {
  nov: 'sf-dbi',
  complaint: 'sf-dbi',
  permit: 'sf-dbi',
  'fire-violation': 'sf-fire-violations',
  'fire-inspection': 'sf-fire-inspections',
  'fire-filing': 'sf-fire-compliance',
  lien: 'sf-recorder',
  '311': 'sf-311',
  tax: 'sf-treasurer',
};

/**
 * How a skill's own monitoring confirms that the person finished the work:
 * - `closed`: the source no longer lists the item as open.
 * - `corrected`: closed, or the source recorded every cited item corrected.
 * - `absent`: closed, or the item no longer derives while the source answered completely.
 * - `none`: no source can confirm; the person's mark is the record.
 */
export const COMPLETION_SIGNALS = ['closed', 'corrected', 'absent', 'none'] as const;
export type CompletionSignal = (typeof COMPLETION_SIGNALS)[number];

/** Days before an item's due date at which it becomes a to-do rather than a calendar entry. */
export const DEFAULT_LEAD_DAYS = 90;

const publicHttpsUrl = z
  .url()
  .max(2000)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password;
    } catch {
      return false;
    }
  }, 'Expected a public HTTPS link');

const completionWindowSchema = z
  .object({
    days: z.number().int().min(1).max(365).optional(),
    businessDays: z.number().int().min(1).max(260).optional(),
  })
  .strict()
  .refine(
    (window) => (window.days === undefined) !== (window.businessDays === undefined),
    'A completion window needs exactly one of days or businessDays',
  );
export type CompletionWindow = z.infer<typeof completionWindowSchema>;

/**
 * What an offer applies to: a legacy kind the skill's collector owns, every
 * generic action item the skill projects (`skill`), or only the action items
 * of one entity type the skill projects (`skill:<entity type>`), so a second
 * jurisdiction can offer different steps per record type.
 */
const offerTargetSchema = z.union([
  z.enum(COMPLIANCE_KINDS),
  z
    .string()
    .regex(/^skill:[a-z][a-z0-9.-]*:[a-z][a-z0-9.-]*$/)
    .max(170),
]);
export type OfferTarget = z.infer<typeof offerTargetSchema>;

/** Button text: one line of plain text, no markup or invisible characters. */
const plainLine = /^[^\p{Cc}\p{Cf}<>]+$/u;

/**
 * What a skill offers a person on one of its items. Phase 1 offers are
 * self-serve: a link to where the work is done, the skill's steps, and the
 * signal the skill's monitoring uses to confirm the result. Declaring an
 * offer never grants the platform any authority to act.
 */
export const offerSchema = z
  .object({
    id: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .max(64),
    mode: z.literal('self-serve'),
    appliesTo: z
      .array(offerTargetSchema)
      .min(1)
      .max(10)
      .refine((targets) => new Set(targets).size === targets.length, 'Repeated offer target'),
    /** The button, a specific verb in sentence case. */
    verb: z.string().trim().min(1).max(40).regex(plainLine, 'Expected plain text'),
    /** `item` opens the item's own official page; otherwise a fixed public page. */
    href: z.union([z.literal('item'), publicHttpsUrl]),
    /** Human steps, three to five lines, bundled with the skill's resources. */
    steps: z.string().regex(/^references\/[a-z0-9-]+\.md$/),
    completion: z.discriminatedUnion('signal', [
      z
        .object({
          signal: z.enum(['closed', 'corrected', 'absent']),
          window: completionWindowSchema,
        })
        .strict(),
      z.object({ signal: z.literal('none') }).strict(),
    ]),
    /**
     * Days before the due date at which a reminder is sent (0 is the due day);
     * the platform applies each person's preferences.
     */
    reminders: z
      .array(z.number().int().min(0).max(365))
      .max(5)
      .refine((days) => new Set(days).size === days.length, 'Repeated reminder day')
      .optional(),
    leadDays: z.number().int().min(1).max(366).optional(),
  })
  .strict()
  .refine(
    (offer) => (offer.reminders ?? []).every((day) => day <= (offer.leadDays ?? DEFAULT_LEAD_DAYS)),
    'A reminder cannot precede the lead time that makes the item a to-do',
  );
export type Offer = z.infer<typeof offerSchema>;

export const offersSchema = z
  .array(offerSchema)
  .max(10)
  .superRefine((offers, ctx) => {
    const ids = offers.map((offer) => offer.id);
    if (new Set(ids).size !== ids.length)
      ctx.addIssue({ code: 'custom', message: 'Duplicate offer id' });
    // One offer per target: a second declaration would be silently discarded.
    const targets = offers.flatMap((offer) => offer.appliesTo);
    if (new Set(targets).size !== targets.length)
      ctx.addIssue({ code: 'custom', message: 'Two offers apply to the same target' });
  });

/** A skill may only offer on kinds its own collector produces. */
export function offerOwnershipErrors(skillId: string, offers: readonly Offer[]) {
  const errors: string[] = [];
  for (const offer of offers)
    for (const target of offer.appliesTo) {
      const owner = isKind(target) ? KIND_OWNERS[target] : skillId;
      if (owner !== skillId)
        errors.push(`${skillId}: offer ${offer.id} applies to ${target}, owned by ${owner}`);
    }
  return errors;
}

const isKind = (target: OfferTarget): target is Exclude<ComplianceKind, 'skill'> =>
  target !== 'skill' && !target.startsWith('skill:');

/**
 * Steps are pinned plain text the app renders itself. Markup, links and images
 * are refused at build time so no skill can inject a page into the sheet.
 */
export function offerStepsError(text: string) {
  const steps = text.trim();
  if (!steps.length || steps.length > 4000) return 'Offer steps must be 1 to 4000 characters';
  if (/<[a-z!/]|\]\(|!\[/i.test(steps) || /[\p{Cc}\p{Cf}]/u.test(steps.replace(/\n/g, '')))
    return 'Offer steps must be plain text without markup, links or images';
  return null;
}

export type ResolvedOffer = {
  skillId: string;
  id: string;
  mode: Offer['mode'];
  verb: string;
  /**
   * The link the button opens: the item's official page or the skill's fixed
   * page. Null when the item's own link is not a record page a person can open,
   * in which case the steps stand alone.
   */
  href: string | null;
  /** Resource path of the steps markdown inside the pinned skill bundle. */
  steps: string;
  completion: Offer['completion'];
  reminders: number[];
  leadDays: number;
};

export type OfferIndex = ReadonlyMap<string, { skillId: string; offer: Offer }>;

/** Offers by the target they apply to; the build guarantees one offer per skill and target. */
export function indexOffers(
  policies: ReadonlyArray<{ id: string; offers?: readonly Offer[] }>,
): OfferIndex {
  const index = new Map<string, { skillId: string; offer: Offer }>();
  for (const policy of policies)
    for (const offer of policy.offers ?? [])
      for (const target of offer.appliesTo) {
        const key = isKind(target) ? target : `${policy.id}/${target}`;
        if (!index.has(key)) index.set(key, { skillId: policy.id, offer });
      }
  return index;
}

export type OfferItem = {
  kind: ComplianceKind;
  skillId: string;
  sourceUrl: string;
  /** Entity type of a generic action item, for `skill:<entity type>` offers. */
  entityType?: string;
  /** Legacy record identity, which resolves links for records whose stored URL is a dataset. */
  record?: { kind?: string; recordId?: string };
};

/** The offer the owning skill declares for an item, with its link resolved under the link policy. */
export function resolveOffer(index: OfferIndex | undefined, item: OfferItem): ResolvedOffer | null {
  if (!index) return null;
  const keys =
    item.kind === 'skill'
      ? [
          ...(item.entityType ? [`${item.skillId}/skill:${item.entityType}`] : []),
          `${item.skillId}/skill`,
        ]
      : [item.kind];
  const match = keys.map((key) => index.get(key)).find((entry) => entry?.skillId === item.skillId);
  if (!match) return null;
  const { skillId, offer } = match;
  return {
    skillId,
    id: offer.id,
    mode: offer.mode,
    verb: offer.verb,
    href: offer.href === 'item' ? itemHref(item) : offer.href,
    steps: `skills/${skillId}/${offer.steps}`,
    completion: offer.completion,
    reminders: [...(offer.reminders ?? [])].sort((a, b) => b - a),
    leadDays: offer.leadDays ?? DEFAULT_LEAD_DAYS,
  };
}

/** The item's own page under the shared link policy, and only over HTTPS: the person may sign in there. */
function itemHref(item: OfferItem) {
  const href = humanSourceUrl(item.sourceUrl, item.record);
  return href?.startsWith('https://') ? href : null;
}

const DAY = 86_400_000;

function parseIsoDay(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) throw new Error(`Expected an ISO date: ${value}`);
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * The last day a mark may wait for the source before it is disputed. Business
 * days skip Saturdays and Sundays only; agency holidays are not modelled, so a
 * workspace that needs more slack lengthens the window instead.
 */
export function completionWindowEnd(from: string, window: CompletionWindow) {
  let time = parseIsoDay(from);
  if (window.days !== undefined) time += window.days * DAY;
  else {
    let remaining = window.businessDays ?? 0;
    while (remaining > 0) {
      time += DAY;
      const weekday = new Date(time).getUTCDay();
      if (weekday !== 0 && weekday !== 6) remaining -= 1;
    }
  }
  return new Date(time).toISOString().slice(0, 10);
}

export type CompletionObservation = {
  /** The item still derives from the latest accepted projection. */
  present: boolean;
  officialOpen?: boolean;
  attention?: 'action' | 'progress' | 'corrected';
  /** The owning source answered completely on the run that produced the observation. */
  coverageComplete: boolean;
};

/**
 * Whether the skill's monitoring confirms the work is done. Absence only counts
 * when the source answered completely; a failed check never closes an item.
 */
export function completionReached(signal: CompletionSignal, current: CompletionObservation) {
  if (signal === 'none') return false;
  if (current.present && current.officialOpen === false) return true;
  if (!current.present) return signal === 'absent' && current.coverageComplete;
  if (signal === 'corrected') return current.attention === 'corrected';
  return false;
}
